<template>
  <a-modal
    title="Enter Formula"
    :visible="visible"
    :confirmLoading="saving"
    :width="'100%'"
    :style="{ top: 0, paddingBottom: 0 }"
    :bodyStyle="{ height: 'calc(100vh - 55px)', overflow: 'auto', padding: '24px' }"
    wrapClassName="fullscreen-modal"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <div class="input-jexl-builder">
      <div class="variable-section">
        <h3>Available Variables</h3>
        <a-checkbox-group v-model="selectedVariables" @change="onVariableChange">
          <a-checkbox
            v-for="item in collectionfeatures"
            :key="item.itemValue"
            :value="item.itemValue">
            {{ item.itemText }}
          </a-checkbox>
        </a-checkbox-group>
      </div>

      <div class="code-section">
        <h3>Generated Function Code</h3>
        <a-textarea 
          v-model="codeContent" 
          :rows="20" 
          class="code-editor"
          placeholder="Please enter or Editfunction code"
        />
        <div style="margin-top: 10px">
          <a-button type="primary" @click="copyCode">Copy Code</a-button>
          <a-button @click="formatCode" style="margin-left: 10px">Format</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { saveOrUpdateSecond } from '@/webpublicapi/collection'

export default {
  name: 'InputJexl',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      default: () => ({})
    },
    collectionfeatures: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedVariables: [],
      codeContent: '',
      saving: false,
      currentEditRecord: null,
      isInitializing: false // Flag whether initializing to avoid triggering variable updates during initialization
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.record) {
        this.initData()
      }
    },
    record: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initData()
        }
      },
      deep: true
    },
    selectedVariables(newVal, oldVal) {
      // If initializing, do not process variable changes
      if (this.isInitializing) {
        return
      }
      
      // If code is empty, generate sample code based on selected variables
      if (!this.codeContent || this.codeContent.trim() === '') {
        this.generateExampleCode()
      } else {
        // If code is not empty, update variable declarations while keeping original formatting
        this.updateVariableDeclarations()
      }
    }
  },
  methods: {
    initData() {
      this.isInitializing = true
      this.currentEditRecord = this.record
      
      // Check if has jexl Formula
      if (this.record.jexl && this.record.jexl.trim()) {
        // If jexl Not empty, from jexl Extract used variables from
        const usedVariables = this.extractVariablesFromJexl(this.record.jexl)
        if (usedVariables.length > 0) {
          this.selectedVariables = usedVariables
        } else {
          // If extraction fails, select the first one by default
          if (this.collectionfeatures && this.collectionfeatures.length > 0) {
            this.selectedVariables = [this.collectionfeatures[0].itemValue]
          }
        }
        // Use existing code
        this.codeContent = this.record.jexl
      } else {
        // If jexl Empty, only select the first variable
        if (this.collectionfeatures && this.collectionfeatures.length > 0) {
          this.selectedVariables = [this.collectionfeatures[0].itemValue]
        }
        // Generate sample code
        this.generateExampleCode()
      }
      
      this.$nextTick(() => {
        this.isInitializing = false
      })
    },
    generateExampleCode() {
      if (this.selectedVariables.length === 0) {
        this.codeContent = ''
        return
      }
      
      // Generate sample code, format:function getLevel(values)
      let code = 'function getLevel(values) {\n'
      code += '  var json = JSON.parse(values);\n'
      
      // Generate for each selected variable var Declaration
      this.selectedVariables.forEach((varName) => {
        code += `  var ${varName} = json.${varName};\n`
      })
      
      code += '\n'
      
      // Use first variable to generate sample condition
      if (this.selectedVariables.length > 0) {
        const firstVar = this.selectedVariables[0]
        code += `  if(${firstVar} > 1) {\n`
        code += `    return 'true';\n`
        code += '  }\n'
      }
      
      code += '}'
      
      this.codeContent = code
    },
    // Update variable declaration part in code
    updateVariableDeclarations() {
      if (!this.codeContent || !this.codeContent.trim()) {
        return
      }
      
      // Check if code is function getLevel Format
      if (!this.codeContent.includes('function getLevel')) {
        this.generateExampleCode()
        return
      }
      
      // Extract function body
      const functionMatch = this.codeContent.match(/function\s+getLevel\s*\([^)]*\)\s*\{([\s\S]*)\}$/)
      if (!functionMatch) {
        return
      }
      
      const functionBody = functionMatch[1]
      
      // Extract line: var json = JSON.parse(values);
      const jsonParseMatch = functionBody.match(/^\s*(var\s+json\s*=\s*JSON\.parse\(values\);\s*)/m)
      const jsonParseLine = jsonParseMatch ? jsonParseMatch[1] : '  var json = JSON.parse(values);\n'
      
      // Extract indentation of first line variable declaration
      const varRegex = /^(\s*)var\s+(\w+)\s*=\s*json\.\w+;/gm
      let match
      let firstVarIndent = '  ' // Default indent is2Spaces
      const existingVarOrder = []
      
      while ((match = varRegex.exec(functionBody)) !== null) {
        const indent = match[1]
        const varName = match[2]
        if (varName !== 'json') {
          if (!existingVarOrder.includes(varName)) {
            existingVarOrder.push(varName)
            // Record indentation of first line variable declaration
            if (existingVarOrder.length === 1) {
              firstVarIndent = indent
            }
          }
        }
      }
      
      // Sort variables: keep existing variable order first, then by collectionfeatures Add new variables in order
      const sortedVariables = []
      const varMap = new Map()
      
      // Add existing variables first (keep original order)
      existingVarOrder.forEach(varName => {
        if (this.selectedVariables.includes(varName)) {
          sortedVariables.push(varName)
          varMap.set(varName, true)
        }
      })
      
      // Then add newly selected variables (according to collectionfeatures Order)
      if (this.collectionfeatures && this.collectionfeatures.length > 0) {
        this.collectionfeatures.forEach(feature => {
          const varName = feature.itemValue
          if (this.selectedVariables.includes(varName) && !varMap.has(varName)) {
            sortedVariables.push(varName)
            varMap.set(varName, true)
          }
        })
      }
      
      // Generate new variable declarations
      let varDeclarations = ''
      sortedVariables.forEach((varName) => {
        varDeclarations += `${firstVarIndent}var ${varName} = json.${varName};\n`
      })
      
      // Extract var Content after declaration (remove all var Declaration line)
      const lines = functionBody.split('\n')
      const afterVarSection = lines.filter(line => !/^\s*var\s+\w+\s*=/.test(line))
      const bodyAfterVars = afterVarSection.join('\n').replace(/^\s*\n+|\n+\s*$/g, '')
      
      // Reassemble code
      let newCode = 'function getLevel(values) {\n'
      newCode += jsonParseLine
      if (varDeclarations) {
        newCode += varDeclarations
      }
      if (bodyAfterVars) {
        newCode += '\n' + bodyAfterVars + '\n'
      } else if (varDeclarations) {
        newCode += '\n'
      }
      newCode += '}'
      
      this.codeContent = newCode
      
      // Call formatting method
      this.formatCode()
    },
    extractVariablesFromJexl(jexl) {
      const variables = []
      if (!jexl || typeof jexl !== 'string') {
        return variables
      }
      
      const availableVars = this.collectionfeatures ? this.collectionfeatures.map(item => item.itemValue) : []
      if (availableVars.length === 0) {
        return variables
      }
      
      // If jexl Is function code, prioritize from var Extract variables from declaration
      if (jexl.includes('var ') || jexl.includes('function')) {
        // Match var variable = json.variable; Mode
        const varMatches = jexl.matchAll(/var\s+(\w+)\s*=\s*json\.\w+/g)
        for (const match of varMatches) {
          const varName = match[1]
          // Check if variable is in available variable list
          if (varName && availableVars.includes(varName) && !variables.includes(varName)) {
            variables.push(varName)
          }
        }
      }
      
      // If from var No variables extracted from declaration, or jexl Is condition expression, extract from conditions
      if (variables.length === 0 || !jexl.includes('var ')) {
        // Find all variable names in jexl (in condition expressions)
        availableVars.forEach(varName => {
          // Use word boundary matching to ensure complete variable name match
          // Match pattern: variable name followed by operator or space
          const regex = new RegExp(`\\b${varName}\\b\\s*([><=!]|\\s)`, 'g')
          if (regex.test(jexl)) {
            if (!variables.includes(varName)) {
              variables.push(varName)
            }
          }
        })
      }
      
      return variables
    },
    onVariableChange() {
      // Imitate jexl1.vue, only update view, do not modify code content
      this.$forceUpdate()
    },
    handleOk() {
      if (!this.codeContent || !this.codeContent.trim()) {
        this.$message.error('Please enter function code')
        return
      }
      
      if (!this.currentEditRecord || !this.currentEditRecord.id) {
        this.$message.error('Missing record info, cannot save')
        return
      }
      
      // Extract from code jexlResult (simple check: if code has return 1 or return true, then result is true)
      const jexlResult = (this.codeContent.includes('return 1') || 
                         this.codeContent.includes('return true') ||
                         this.codeContent.includes("return 'true'")) ? 'true' : 'false'
      
      // Save formula
      this.saving = true
      const payload = {
        ...this.currentEditRecord,
        jexl: this.codeContent.trim(),
        jexlResult: jexlResult
      }
      
      saveOrUpdateSecond(payload)
        .then((res) => {
          if (res.code === 1000) {
            this.$message.success('Formula saved successfully')
            this.$emit('ok', payload)
            this.$emit('update:visible', false)
          } else {
            this.$message.error(res.msg || 'Save failed')
          }
          this.saving = false
        })
        .catch((e) => {
          this.saving = false
          this.$message.error(e.message || 'Save failed')
        })
    },
    handleCancel() {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    copyCode() {
      const textarea = document.createElement('textarea')
      textarea.value = this.codeContent
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$message.success('Code copied to clipboard')
    },
    formatCode() {
      // Conservative code formatting: only adjust indentation, do not change code structure
      try {
        if (!this.codeContent || !this.codeContent.trim()) {
          return
        }
        
        const lines = this.codeContent.split('\n')
        const formattedLines = []
        let indentLevel = 0
        const indentSize = 2 // 2spaces indent
        for (let i = 0; i < lines.length; i++) {
          let line = lines[i]
          const trimmed = line.trim()
          
          // Skip empty lines, but keep them
          if (trimmed === '') {
            formattedLines.push('')
            continue
          }
          
          // If encounter }, first reduce indentation
          if (trimmed.startsWith('}')) {
            indentLevel = Math.max(0, indentLevel - 1)
          }
          
          // Add current line indentation
          const indent = ' '.repeat(indentLevel * indentSize)
          formattedLines.push(indent + trimmed)
          
          // If encounter {, add indentation (after adding lines)
          if (trimmed.includes('{') && !trimmed.includes('}')) {
            indentLevel++
          }
        }
        
        // Reassemble code, keeping original line break style
        this.codeContent = formattedLines.join('\n')
        // this.$message.success('Code formatted')
      } catch (e) {
        // this.$message.warning('Formatting failed, please adjust manually')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.input-jexl-builder {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.variable-section {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
  }
}

.code-section {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
  }
}

.code-editor {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
}

/* Fullscreen modal Style */
/deep/ .fullscreen-modal {
  .ant-modal {
    max-width: 100% !important;
    top: 0 !important;
    padding-bottom: 0 !important;
    margin: 0 !important;
  }
  .ant-modal-content {
    width: 100vw !important;
    height: 100vh !important;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    overflow: auto;
  }
}
</style>

